import { useState } from 'react';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Group,
  Modal,
  Stack,
  Table,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { Link } from 'react-router';
import { ISLERIM_HEDEF_HARITASI } from '@isg/validation';
import type { PtwPendingApproval } from '@isg/shared-types';
import { useBekleyenOnaylar, useIzinDetay, useOnayKarari, useOnayUygunlugu } from '../api/queries';
import {
  PTW_DURUM_ETIKET,
  PTW_ONAY_KAPI_ETIKET,
  PTW_ONAY_SONUC_ETIKET,
  SAPKA_ETIKET,
} from '../i18n/tr';
import { KunyeTablosu, OnaylaDugmesi, UygunlukListesi, damga } from './izin-parts';
import { BosListeNotu, QueryStatus } from './parts';
import { ANLAM } from '../theme';
import { zamanGoster } from '../lib/format';

/**
 * BEKLEYEN ONAYLARIM (F3·S2; F3·S4'te TALEP DETAYINA genişledi).
 *
 * Havuz içinde onaycı SEÇİMİ yoktur: aynı satır havuzdaki herkeste görünür,
 * ilk karar veren basamağı kapatır. Üç sonuç: onayla · revizyon iste (talep
 * başa döner, TÜM onaylar sıfırlanır = yeni tur) · reddet (süreç biter).
 *
 * F3·S4 GENİŞLEMESİ:
 *  · liste ÜÇ KAPIYI taşır (açılış · uzatma · kapanış) — kapı satırın kendi
 *    alanından gelir, ekran ayrı liste tutmaz (S0-K4 simetrisi),
 *  · karar kutusu talebin TAMAMINI ve UYGUNLUK SONUÇLARINI gösterir: onaycı
 *    imzalamadan önce neye imza attığını görür. Sonuçlar API'den okunur,
 *    ekran yeniden hesaplamaz (§2.16).
 */
/**
 * KARAR SEÇİMİ — karar kutusunun girdisi. ✏️ PTW onay düğmesi (14.09.2026 —
 * K-62): izin sayfasının Onaylar bloğu da AYNI kutuyu açar (ikinci karar yolu
 * yazılmadı); basamak satırından bu şekle çevrilir.
 */
export interface KararSecimi {
  satir: PtwPendingApproval;
  sonuc: 'approved' | 'revision_requested' | 'rejected';
}

export function BekleyenOnaylarScreen(): React.JSX.Element {
  const bekleyenler = useBekleyenOnaylar();
  const [karar, setKarar] = useState<KararSecimi | null>(null);

  return (
    <Stack>
      <Title order={3}>Bekleyen onaylarım</Title>
      <Text size="sm" c="dimmed">
        Basamaklar paralel koşar: hepsi onaylayınca izin açılır. Aynı satır havuzdaki herkeste
        görünür — ilk karar veren basamağı kapatır ve imza kayda geçer.
      </Text>
      <QueryStatus queries={[bekleyenler]} />
      {bekleyenler.isSuccess && bekleyenler.data.length === 0 && (
        <BosListeNotu mesaj="Onayınızı bekleyen iş izni yok." />
      )}
      {bekleyenler.isSuccess && bekleyenler.data.length > 0 && (
        <Table striped highlightOnHover data-testid="bekleyen-onay-listesi">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>İşyeri / lokasyon</Table.Th>
              <Table.Th>Talep eden</Table.Th>
              <Table.Th>Geçerlilik</Table.Th>
              <Table.Th>Kapı</Table.Th>
              <Table.Th>Basamağım</Table.Th>
              <Table.Th>Durum</Table.Th>
              <Table.Th>Karar</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {bekleyenler.data.map((satir) => (
              <Table.Tr
                key={`${satir.permit.id}-${satir.chainStepId}`}
                data-testid="bekleyen-onay-satiri"
                // Satırın hangi izne ait olduğu testlerde de ayırt edilebilir
                // olmalı: aynı işyerinde birden çok izin bekleyebilir.
                data-permit={satir.permit.id}
              >
                <Table.Td>
                  <Anchor
                    component={Link}
                    to={ISLERIM_HEDEF_HARITASI.onay(satir.permit.id)}
                    size="sm"
                    data-testid="bekleyen-onay-bagi"
                  >
                    {satir.permit.workplaceAd}
                  </Anchor>
                  <Text size="xs" c="dimmed">
                    {satir.permit.locationAd}
                  </Text>
                  {damga(satir.permit)}
                </Table.Td>
                <Table.Td>{satir.permit.talepEdenAd}</Table.Td>
                <Table.Td>
                  <Text size="xs">
                    {zamanGoster(satir.permit.gecerlilikBaslangic)} →{' '}
                    {zamanGoster(satir.permit.gecerlilikBitis)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  {/* ÜÇ KAPI AYNI LİSTEDE (S0-K4): kapı satırın alanından
                      gelir — uzatma ve kapanış için ikinci ekran açılmadı. */}
                  <Badge variant="outline" data-testid="onay-kapisi">
                    {PTW_ONAY_KAPI_ETIKET[satir.kapi]}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Badge variant={satir.izinVeren ? 'filled' : 'light'}>
                    {satir.stepAd} · {SAPKA_ETIKET[satir.sapka]}
                  </Badge>
                  {satir.turNo > 1 && (
                    <Text size="xs" c="dimmed">
                      {satir.turNo}. tur
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>{PTW_DURUM_ETIKET[satir.permit.durum]}</Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <OnaylaDugmesi
                      permitId={satir.permit.id}
                      testId="onay-onayla"
                      onClick={() => setKarar({ satir, sonuc: 'approved' })}
                    />
                    <Button
                      size="xs"
                      variant="default"
                      data-testid="onay-revizyon"
                      onClick={() => setKarar({ satir, sonuc: 'revision_requested' })}
                    >
                      {PTW_ONAY_SONUC_ETIKET.revision_requested}
                    </Button>
                    <Button
                      size="xs"
                      variant="light"
                      color={ANLAM.engel}
                      data-testid="onay-red"
                      onClick={() => setKarar({ satir, sonuc: 'rejected' })}
                    >
                      {PTW_ONAY_SONUC_ETIKET.rejected}
                    </Button>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
      <KararKutusu karar={karar} onClose={() => setKarar(null)} />
    </Stack>
  );
}

/**
 * KARAR KUTUSU — TEK KARAR YOLU. İşlerim listesi ve izin sayfasının Onaylar
 * bloğu (K-62) bunu açar; uygunluk okuması ve gerekçe kuralı burada bir kez
 * yaşar.
 */
export function KararKutusu({
  karar,
  onClose,
}: {
  karar: KararSecimi | null;
  onClose: () => void;
}): React.JSX.Element {
  const ver = useOnayKarari();
  const [gerekce, setGerekce] = useState('');
  // Onaycı NEYE imza attığını görür (S4): talebin çekirdeği + uygunluk
  // kalemleri. İkisi de sunucudan okunur — karar kutusu kural hesaplamaz.
  const permitId = karar?.satir.permit.id ?? null;
  const detay = useIzinDetay(permitId);
  // ✏️ P-2/P-5 (28.08.2026): ANLIK uygunluk ucu (`/eligibility`) yerine ONAY
  // KAPISININ okuması. İki fark, ikisi de kilitli kararın gereği:
  //  · sonuç İKİYE AYRILMIŞ gelir (izin / onaylayan) — tek rozet ikisini
  //    birden temsil ediyormuş gibi durunca kullanıcı çelişki görüyordu,
  //  · `engeller` KARAR UCUYLA AYNI fonksiyondan gelir; düğmenin pasifliği
  //    ondan okunur ve ekran ikinci kural yazmaz.
  const uygunluk = useOnayUygunlugu(permitId);

  // MADDE 49 (Kapsam §16.7.2): revizyon + red GEREKÇE İSTER, onay istemez.
  // Kural sunucuda (şema + motor + DB CHECK); ekran onu yalnız GÖRÜNÜR kılar
  // — ikinci bir "hangi sonuçta gerekçe ister" listesi tutulmaz.
  const gerekceZorunlu = karar !== null && karar.sonuc !== 'approved';
  const gerekceEksik = gerekceZorunlu && gerekce.trim() === '';

  // P-2 — ONAYDA UYGUNSUZLUK KESİN ENGELDİR. "Kural VAR MI" müşterinin
  // kararıdır (şartı kaldırırsa kimse engellenmez); "kural VARKEN
  // geçilebilir mi" değildir — geçilebilirse kural süse döner.
  // ⚠️ Yalnız ONAY bloklanır: revizyon ve red DÜZELTME YOLUDUR; onları da
  // kapatmak uygunsuz talebi ekranda kilitlerdi.
  const engeller = uygunluk.data?.engeller ?? [];
  const onayEngelli = karar?.sonuc === 'approved' && engeller.length > 0;

  const aciklama =
    karar?.sonuc === 'revision_requested'
      ? 'Revizyon isteği izni talebe geri döndürür ve TÜM onayları sıfırlar (yeni tur açılır; verilmiş imzalar kayıtta kalır).'
      : karar?.sonuc === 'rejected'
        ? 'Red süreci bitirir: iş yapılacaksa YENİ talep açılması gerekir.'
        : 'Onayınız bu basamağı kapatır. Zincirin tüm basamakları onaylayınca izin açılışa geçer.';

  return (
    <Modal
      opened={karar !== null}
      onClose={onClose}
      title={
        karar
          ? `${PTW_ONAY_SONUC_ETIKET[karar.sonuc]} — ${PTW_ONAY_KAPI_ETIKET[karar.satir.kapi]} · ${karar.satir.permit.workplaceAd}`
          : ''
      }
      size="lg"
    >
      <Stack>
        <Text size="sm" c="dimmed">
          {aciklama}
        </Text>
        {detay.isSuccess && (
          <KunyeTablosu
            satirlar={[
              { etiket: 'Lokasyon', deger: detay.data.permit.locationAd },
              { etiket: 'Talep eden', deger: detay.data.permit.talepEdenAd },
              { etiket: 'Güncel sorumlu', deger: detay.data.guncelSorumluAd ?? '—' },
              {
                etiket: 'Geçerlilik',
                deger: `${zamanGoster(detay.data.permit.gecerlilikBaslangic)} → ${zamanGoster(detay.data.permit.gecerlilikBitis)}`,
              },
              {
                etiket: 'İzin türleri',
                deger: detay.data.izinTurleri.map((t) => t.ad).join(', ') || '—',
              },
              {
                etiket: 'Katılımcılar',
                deger:
                  detay.data.katilimcilar.map((k) => `${k.personAd} (${k.gorevAd})`).join(', ') ||
                  '—',
              },
              {
                etiket: 'İş ekipmanları',
                deger: detay.data.ekipmanlar.map((e) => e.ad).join(', ') || '—',
              },
              // MADDE 52: uzatma turunda onaycı NEYE imza attığını görür —
              // talep edilen YENİ BİTİŞ ve gerekçe. Satır yalnız açık talep
              // varsa çıkar; talep yoksa zaten uzatma kapısı açılmaz.
              ...(detay.data.acikUzatmaTalebi
                ? [
                    {
                      etiket: 'Uzatma talebi',
                      deger: `${zamanGoster(detay.data.acikUzatmaTalebi.yeniBitis)} tarihine — ${detay.data.acikUzatmaTalebi.gerekce}`,
                    },
                  ]
                : []),
            ]}
          />
        )}
        {/* P-5 (madde 125) — İKİ AYRI KONTROL, İKİ AYRI BAŞLIK. Üstteki İZNİN
            uygunluğu (katılımcı · ekipman · sağlık · yüklenici), alttaki
            ONAYLAYANIN yetkinliği (K3). Tek rozet ikisini birden temsil
            ETMEZ; ayrım kural kodundan gelir, ekranın tahmininden değil. */}
        {uygunluk.isSuccess && uygunluk.data !== null && (
          <>
            <UygunlukListesi
              baslik="İzin uygunluğu"
              testId="onay-uygunluk-izin"
              sonuc={uygunluk.data.izin}
            />
            {/* ✏️ K-258: blok YALNIZ izin verenden aranan şart tanımlıysa gelir
                (sunucu `null` döner) — şart yokken hiçbir şey çizilmez. */}
            {uygunluk.data.onaylayan !== null && (
              <UygunlukListesi
                baslik="Onaylayan uygunluğu"
                testId="onay-uygunluk-onaylayan"
                sonuc={uygunluk.data.onaylayan}
              />
            )}
          </>
        )}
        {onayEngelli && (
          <Alert
            color={ANLAM.engel}
            title="Onay verilemez — uygunsuz kalem var"
            role="alert"
            data-testid="onay-engel-notu"
          >
            <Text size="sm">
              Yukarıdaki {engeller.length} uygunsuz kalem giderilmeden bu izne onay verilemez.
              Kalemi düzeltebilir, talebi revizyona gönderebilir ya da reddedebilirsiniz.
            </Text>
          </Alert>
        )}
        <Textarea
          label="Gerekçe"
          withAsterisk={gerekceZorunlu}
          description={
            gerekceZorunlu
              ? 'Zorunlu: gerekçesiz revizyon talep sahibini, gerekçesiz red raporu kör bırakır.'
              : 'Kayda geçer; onayda zorunlu değildir.'
          }
          data-testid="onay-gerekce"
          value={gerekce}
          onChange={(event) => setGerekce(event.currentTarget.value)}
        />
        {ver.isError && (
          <Text size="sm" c={ANLAM.engel} role="alert">
            {ver.error.message}
          </Text>
        )}
        <Button
          data-testid="onay-karar-onayla"
          // KA-9: uygunluk yüklenirken onay BEKLER (boş varsayılan bir an açık gösterirdi).
          disabled={
            gerekceEksik || onayEngelli || (karar?.sonuc === 'approved' && uygunluk.isPending)
          }
          loading={ver.isPending}
          onClick={() => {
            if (!karar) return;
            ver.mutate(
              {
                permitId: karar.satir.permit.id,
                input: {
                  // Kapı SATIRDAN gelir: aynı uç, aynı motor — uzatma ve
                  // kapanış için ikinci karar yolu açılmadı (§2.6d).
                  kapi: karar.satir.kapi,
                  sonuc: karar.sonuc,
                  gerekce: gerekce.trim() === '' ? null : gerekce.trim(),
                },
              },
              {
                onSuccess: () => {
                  setGerekce('');
                  onClose();
                },
              },
            );
          }}
        >
          Kararı kaydet
        </Button>
      </Stack>
    </Modal>
  );
}
